import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';

const Settings = () => {
  const { isDark, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // SMS Services state
  const [smsServices, setSmsServices] = useState([]);
  const [smsServiceForms, setSmsServiceForms] = useState({});

  // SMS Timing state
  const [smsTimings, setSmsTimings] = useState([]);

  // SMS Templates state
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    category: 'general',
    template_text: '',
  });

  // Locations state
  const [locations, setLocations] = useState([]);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  // API Base URL - should be configured in environment
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rccgtr.net/api';

  useEffect(() => {
    loadSmsServices();
    loadSmsTimings();
    loadSmsTemplates();
    loadLocations();
  }, []);

  const loadSmsServices = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/sms/services`);
      const data = await response.json();

      if (data.success) {
        setSmsServices(data.data);
        // Initialize form state with current values
        const forms = {};
        data.data.forEach(service => {
          forms[service.id] = {
            is_enabled: service.is_enabled,
            api_key: service.api_key || '',
            api_secret: service.api_secret || '',
            from_number: service.from_number || '',
            from_name: service.from_name || '',
            username: service.username || '',
          };
        });
        setSmsServiceForms(forms);
      } else {
        setMessage('Failed to load SMS services');
      }
    } catch (error) {
      console.error('Error loading SMS services:', error);
      setMessage('Error loading SMS services');
    } finally {
      setLoading(false);
    }
  };

  const loadSmsTimings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/sms/timing`);
      const data = await response.json();

      if (data.success) {
        setSmsTimings(data.data);
      } else {
        setMessage('Failed to load SMS timing settings');
      }
    } catch (error) {
      console.error('Error loading SMS timing settings:', error);
      setMessage('Error loading SMS timing settings');
    } finally {
      setLoading(false);
    }
  };

  const loadSmsTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/sms/templates`);
      const data = await response.json();

      if (data.success) {
        setSmsTemplates(data.data);
      } else {
        setMessage('Failed to load SMS templates');
      }
    } catch (error) {
      console.error('Error loading SMS templates:', error);
      setMessage('Error loading SMS templates');
    } finally {
      setLoading(false);
    }
  };

  const updateSmsServiceForm = (serviceId, field, value) => {
    setSmsServiceForms(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [field]: value,
      },
    }));
  };

  const saveSmsService = async serviceId => {
    try {
      setLoading(true);
      const formData = smsServiceForms[serviceId];

      const response = await fetch(`${API_BASE_URL}/sms/services/${serviceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isEnabled: formData.is_enabled,
          apiKey: formData.api_key,
          apiSecret: formData.api_secret,
          fromNumber: formData.from_number,
          fromName: formData.from_name,
          username: formData.username,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage('SMS service updated successfully!');
        loadSmsServices(); // Reload to get updated data
      } else {
        setMessage('Failed to update SMS service');
      }
    } catch (error) {
      console.error('Error updating SMS service:', error);
      setMessage('Error updating SMS service');
    } finally {
      setLoading(false);
    }
  };

  const updateSmsTiming = async (category, updates) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/sms/timing/${category}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (data.success) {
        setMessage('SMS timing setting updated successfully!');
        loadSmsTimings(); // Reload to get updated data
      } else {
        setMessage('Failed to update SMS timing setting');
      }
    } catch (error) {
      console.error('Error updating SMS timing setting:', error);
      setMessage('Error updating SMS timing setting');
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async templateData => {
    try {
      setLoading(true);
      const url = editingTemplate
        ? `${API_BASE_URL}/sms/templates/${editingTemplate.id}`
        : `${API_BASE_URL}/sms/templates`;

      const method = editingTemplate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(templateData),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(
          editingTemplate ? 'Template updated successfully!' : 'Template created successfully!'
        );
        setShowTemplateForm(false);
        setEditingTemplate(null);
        setNewTemplate({ name: '', category: 'general', template_text: '' });
        loadSmsTemplates();
      } else {
        setMessage('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setMessage('Error saving template');
    } finally {
      setLoading(false);
    }
  };

  const editTemplate = template => {
    setEditingTemplate(template);
    setNewTemplate({
      name: template.name,
      category: template.category,
      template_text: template.template_text,
    });
    setShowTemplateForm(true);
  };

  const cancelTemplateEdit = () => {
    setShowTemplateForm(false);
    setEditingTemplate(null);
    setNewTemplate({ name: '', category: 'general', template_text: '' });
  };

  const getCategoryDisplayName = category => {
    const names = {
      first_service_first_timer: 'First Service - First Time Visitor',
      first_service_returning_member: 'First Service - Returning Member',
      second_service_first_timer: 'Second Service - First Time Visitor',
      second_service_returning_member: 'Second Service - Returning Member',
      sunday_reminder: 'Sunday Service Reminder',
      midweek_reminder: 'Midweek Service Reminder',
      birthday: 'Birthday Greeting',
    };
    return names[category] || category;
  };

  const getServiceDisplayName = serviceName => {
    const names = {
      first_sunday: 'First Sunday Service (07:30 AM)',
      second_sunday: 'Second Sunday Service (09:30 AM)',
      midweek: 'Midweek Service (Wednesday 6:00 PM)',
      twilio: 'Twilio',
      termii: 'Termii',
      africas_talking: "Africa's Talking",
    };
    return names[serviceName] || serviceName;
  };

  // Location management functions
  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/locations`);
      const data = await response.json();

      if (data.success) {
        setLocations(data.data);
      } else {
        setMessage('Failed to load locations');
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      setMessage('Error loading locations');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSubmit = async e => {
    e.preventDefault();
    try {
      setLoading(true);
      const url = editingLocation
        ? `${API_BASE_URL}/locations/${editingLocation.id}`
        : `${API_BASE_URL}/locations`;

      const method = editingLocation ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLocation),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(
          editingLocation ? 'Location updated successfully' : 'Location created successfully'
        );
        loadLocations();
        setShowLocationForm(false);
        setEditingLocation(null);
        setNewLocation({ name: '', description: '', is_active: true });
      } else {
        setMessage(data.error || 'Failed to save location');
      }
    } catch (error) {
      console.error('Error saving location:', error);
      setMessage('Error saving location');
    } finally {
      setLoading(false);
    }
  };

  const handleEditLocation = location => {
    setEditingLocation(location);
    setNewLocation({
      name: location.name,
      description: location.description || '',
      is_active: location.is_active,
    });
    setShowLocationForm(true);
  };

  const handleDeleteLocation = async id => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/locations/${id}`, {
          method: 'DELETE',
        });

        const data = await response.json();

        if (data.success) {
          setMessage('Location deleted successfully');
          loadLocations();
        } else {
          setMessage(data.error || 'Failed to delete location');
        }
      } catch (error) {
        console.error('Error deleting location:', error);
        setMessage('Error deleting location');
      } finally {
        setLoading(false);
      }
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: '⚙️' },
    { id: 'sms', name: 'SMS Services', icon: '📱' },
    { id: 'timing', name: 'SMS Timing', icon: '⏰' },
    { id: 'templates', name: 'SMS Templates', icon: '📝' },
    { id: 'locations', name: 'Locations', icon: '📍' },
  ];

  return (
    <Layout title="Settings">
      <div className="max-w-6xl mx-auto">
        {/* Message Display */}
        {message && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              message.includes('successfully')
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}
          >
            {message}
            <button onClick={() => setMessage('')} className="float-right font-bold">
              ×
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                <span>{tab.icon}</span>
                <span className="hidden sm:inline">{tab.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Loading Spinner */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        )}

        {/* General Settings Tab */}
        {activeTab === 'general' && (
          <div
            className={`rounded-xl shadow-lg p-6 ${
              isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
            }`}
          >
            <h2 className="text-xl font-semibold mb-4">General Settings</h2>

            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="font-medium">Theme</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Switch between light and dark mode
                  </p>
                </div>
                <button
                  onClick={toggleTheme}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    isDark
                      ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  }`}
                >
                  {isDark ? 'Switch to Light' : 'Switch to Dark'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SMS Services Tab */}
        {activeTab === 'sms' && (
          <div className="space-y-6">
            {smsServices.map(service => {
              const formData = smsServiceForms[service.id] || {};
              return (
                <div
                  key={service.id}
                  className={`rounded-xl shadow-lg p-6 ${
                    isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">
                      {getServiceDisplayName(service.service_name)} SMS Service
                    </h3>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.is_enabled || false}
                        onChange={e =>
                          updateSmsServiceForm(service.id, 'is_enabled', e.target.checked)
                        }
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                        disabled={loading}
                      />
                      <span className="text-sm">Enable</span>
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">API Key</label>
                      <input
                        type="password"
                        value={formData.api_key || ''}
                        onChange={e => updateSmsServiceForm(service.id, 'api_key', e.target.value)}
                        placeholder="Enter API Key"
                        className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-500 ${
                          isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">API Secret</label>
                      <input
                        type="password"
                        value={formData.api_secret || ''}
                        onChange={e =>
                          updateSmsServiceForm(service.id, 'api_secret', e.target.value)
                        }
                        placeholder="Enter API Secret"
                        className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-500 ${
                          isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        disabled={loading}
                      />
                    </div>
                    {service.service_name === 'twilio' && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2">From Number</label>
                        <input
                          type="text"
                          value={formData.from_number || ''}
                          onChange={e =>
                            updateSmsServiceForm(service.id, 'from_number', e.target.value)
                          }
                          placeholder="Enter Twilio phone number"
                          className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-500 ${
                            isDark
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          disabled={loading}
                        />
                      </div>
                    )}
                    {(service.service_name === 'termii' ||
                      service.service_name === 'africas_talking') && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2">
                          {service.service_name === 'termii' ? 'From Name' : 'Username'}
                        </label>
                        <input
                          type="text"
                          value={formData.from_name || formData.username || ''}
                          onChange={e =>
                            updateSmsServiceForm(
                              service.id,
                              service.service_name === 'termii' ? 'from_name' : 'username',
                              e.target.value
                            )
                          }
                          placeholder={`Enter ${
                            service.service_name === 'termii' ? 'sender name' : 'username'
                          }`}
                          className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-500 ${
                            isDark
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          disabled={loading}
                        />
                      </div>
                    )}
                  </div>

                  {/* Save Button */}
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => saveSmsService(service.id)}
                      disabled={loading}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* SMS Timing Tab */}
        {activeTab === 'timing' && (
          <div
            className={`rounded-xl shadow-lg p-6 ${
              isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
            }`}
          >
            <h2 className="text-xl font-semibold mb-4">SMS Category Timing Settings</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Configure when automatic SMS messages should be sent for different categories across
              our two Sunday services
            </p>

            {/* First Service Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-indigo-600 dark:text-indigo-400 border-b border-indigo-200 dark:border-indigo-700 pb-2">
                🏛️ Sunday First Service (10:30 AM)
              </h3>
              <div className="space-y-4">
                {smsTimings
                  .filter(timing => timing.service_name === 'first_sunday')
                  .map(timing => (
                    <div
                      key={timing.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">{getCategoryDisplayName(timing.category)}</h4>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={timing.is_enabled}
                            onChange={e =>
                              updateSmsTiming(timing.category, { isEnabled: e.target.checked })
                            }
                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                            disabled={loading}
                          />
                          <span className="text-sm">Enable</span>
                        </label>
                      </div>

                      {timing.is_enabled && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Time</label>
                            <input
                              type="time"
                              value={timing.send_time}
                              onChange={e =>
                                updateSmsTiming(timing.category, { sendTime: e.target.value })
                              }
                              className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-500 ${
                                isDark
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              disabled={loading}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Day</label>
                            <select
                              value={timing.send_day}
                              onChange={e =>
                                updateSmsTiming(timing.category, { sendDay: e.target.value })
                              }
                              className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-500 ${
                                isDark
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              disabled={loading}
                            >
                              <option value="daily">Daily</option>
                              <option value="sunday">Sunday</option>
                              <option value="monday">Monday</option>
                              <option value="tuesday">Tuesday</option>
                              <option value="wednesday">Wednesday</option>
                              <option value="thursday">Thursday</option>
                              <option value="friday">Friday</option>
                              <option value="saturday">Saturday</option>
                            </select>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-sm font-medium mb-2">Template</label>
                            <select
                              value={timing.template_id || ''}
                              onChange={e =>
                                updateSmsTiming(timing.category, {
                                  templateId: e.target.value || null,
                                })
                              }
                              className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-500 ${
                                isDark
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              disabled={loading}
                            >
                              <option value="">Select Template</option>
                              {smsTemplates.map(template => (
                                <option key={template.id} value={template.id}>
                                  {template.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>

            {/* Second Service Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400 border-b border-green-200 dark:border-green-700 pb-2">
                ⛪ Sunday Second Service (12:30 AM)
              </h3>
              <div className="space-y-4">
                {smsTimings
                  .filter(timing => timing.service_name === 'second_sunday')
                  .map(timing => (
                    <div
                      key={timing.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-green-50 dark:bg-green-900/20"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">{getCategoryDisplayName(timing.category)}</h4>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={timing.is_enabled}
                            onChange={e =>
                              updateSmsTiming(timing.category, { isEnabled: e.target.checked })
                            }
                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                            disabled={loading}
                          />
                          <span className="text-sm">Enable</span>
                        </label>
                      </div>

                      {timing.is_enabled && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Time</label>
                            <input
                              type="time"
                              value={timing.send_time}
                              onChange={e =>
                                updateSmsTiming(timing.category, { sendTime: e.target.value })
                              }
                              className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 ${
                                isDark
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              disabled={loading}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Day</label>
                            <select
                              value={timing.send_day}
                              onChange={e =>
                                updateSmsTiming(timing.category, { sendDay: e.target.value })
                              }
                              className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 ${
                                isDark
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              disabled={loading}
                            >
                              <option value="daily">Daily</option>
                              <option value="sunday">Sunday</option>
                              <option value="monday">Monday</option>
                              <option value="tuesday">Tuesday</option>
                              <option value="wednesday">Wednesday</option>
                              <option value="thursday">Thursday</option>
                              <option value="friday">Friday</option>
                              <option value="saturday">Saturday</option>
                            </select>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-sm font-medium mb-2">Template</label>
                            <select
                              value={timing.template_id || ''}
                              onChange={e =>
                                updateSmsTiming(timing.category, {
                                  templateId: e.target.value || null,
                                })
                              }
                              className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 ${
                                isDark
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              disabled={loading}
                            >
                              <option value="">Select Template</option>
                              {smsTemplates.map(template => (
                                <option key={template.id} value={template.id}>
                                  {template.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>

            {/* General Service Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-purple-600 dark:text-purple-400 border-b border-purple-200 dark:border-purple-700 pb-2">
                🔔 General Service Categories
              </h3>
              <div className="space-y-4">
                {smsTimings
                  .filter(
                    timing =>
                      timing.service_name === 'midweek' ||
                      timing.category === 'sunday_reminder' ||
                      timing.category === 'birthday'
                  )
                  .map(timing => (
                    <div
                      key={timing.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-purple-50 dark:bg-purple-900/20"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">{getCategoryDisplayName(timing.category)}</h4>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={timing.is_enabled}
                            onChange={e =>
                              updateSmsTiming(timing.category, { isEnabled: e.target.checked })
                            }
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            disabled={loading}
                          />
                          <span className="text-sm">Enable</span>
                        </label>
                      </div>

                      {timing.is_enabled && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Time</label>
                            <input
                              type="time"
                              value={timing.send_time}
                              onChange={e =>
                                updateSmsTiming(timing.category, { sendTime: e.target.value })
                              }
                              className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-purple-500 ${
                                isDark
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              disabled={loading}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Day</label>
                            <select
                              value={timing.send_day}
                              onChange={e =>
                                updateSmsTiming(timing.category, { sendDay: e.target.value })
                              }
                              className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-purple-500 ${
                                isDark
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              disabled={loading}
                            >
                              <option value="daily">Daily</option>
                              <option value="sunday">Sunday</option>
                              <option value="monday">Monday</option>
                              <option value="tuesday">Tuesday</option>
                              <option value="wednesday">Wednesday</option>
                              <option value="thursday">Thursday</option>
                              <option value="friday">Friday</option>
                              <option value="saturday">Saturday</option>
                            </select>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-sm font-medium mb-2">Template</label>
                            <select
                              value={timing.template_id || ''}
                              onChange={e =>
                                updateSmsTiming(timing.category, {
                                  templateId: e.target.value || null,
                                })
                              }
                              className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-purple-500 ${
                                isDark
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              disabled={loading}
                            >
                              <option value="">Select Template</option>
                              {smsTemplates.map(template => (
                                <option key={template.id} value={template.id}>
                                  {template.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>

            {/* Service Information */}
            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
              <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                ℹ️ Service Information
              </h4>
              <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <p>
                  <strong>First Sunday Service:</strong> 10:30 AM - First Timers & Returning Members
                </p>
                <p>
                  <strong>Second Sunday Service:</strong> 12:30 PM - First Timers & Returning
                  Members
                </p>
                <p>
                  <strong>Midweek Service:</strong> Wednesday 12:00 PM - General reminders
                </p>
                <p>
                  <strong>Birthday SMS:</strong> Daily at 8:00 AM for birthday people
                </p>
              </div>
            </div>
          </div>
        )}

        {/* SMS Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            {/* Templates List */}
            <div
              className={`rounded-xl shadow-lg p-6 ${
                isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">SMS Templates</h2>
                <button
                  onClick={() => setShowTemplateForm(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Add New Template
                </button>
              </div>

              <div className="space-y-4">
                {smsTemplates.map(template => (
                  <div
                    key={template.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium">{template.name}</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => editTemplate(template)}
                          className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 text-sm font-medium"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Category: {template.category}
                    </p>
                    <p className="text-sm">{template.template_text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Template Form */}
            {showTemplateForm && (
              <div
                className={`rounded-xl shadow-lg p-6 ${
                  isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
                }`}
              >
                <h3 className="text-lg font-semibold mb-4">
                  {editingTemplate ? 'Edit Template' : 'Add New Template'}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Template Name</label>
                    <input
                      type="text"
                      value={newTemplate.name}
                      onChange={e => setNewTemplate({ ...newTemplate, name: e.target.value })}
                      placeholder="Enter template name"
                      className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-500 ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Category</label>
                    <select
                      value={newTemplate.category}
                      onChange={e => setNewTemplate({ ...newTemplate, category: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-500 ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="welcome">Welcome</option>
                      <option value="reminder">Reminder</option>
                      <option value="birthday">Birthday</option>
                      <option value="announcement">Announcement</option>
                      <option value="general">General</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Template Text</label>
                    <textarea
                      value={newTemplate.template_text}
                      onChange={e =>
                        setNewTemplate({ ...newTemplate, template_text: e.target.value })
                      }
                      placeholder="Enter template text"
                      rows={4}
                      className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-500 ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => saveTemplate(newTemplate)}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                      disabled={loading || !newTemplate.name || !newTemplate.template_text}
                    >
                      {loading
                        ? 'Saving...'
                        : editingTemplate
                        ? 'Update Template'
                        : 'Save Template'}
                    </button>
                    <button
                      onClick={cancelTemplateEdit}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Locations Tab */}
        {activeTab === 'locations' && (
          <div className="space-y-6">
            {/* Locations List */}
            <div
              className={`rounded-xl shadow-lg p-6 ${
                isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
              }`}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Locations</h2>
                <button
                  onClick={() => {
                    setEditingLocation(null);
                    setNewLocation({ name: '', description: '', is_active: true });
                    setShowLocationForm(true);
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Add Location
                </button>
              </div>

              {locations.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No locations found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className={`${isDark ? 'border-gray-700' : 'border-gray-200'} border-b`}>
                        <th className="text-left py-3 px-4 font-medium">Name</th>
                        <th className="text-left py-3 px-4 font-medium">Description</th>
                        <th className="text-left py-3 px-4 font-medium">Status</th>
                        <th className="text-left py-3 px-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locations.map(location => (
                        <tr
                          key={location.id}
                          className={`${isDark ? 'border-gray-700' : 'border-gray-200'} border-b`}
                        >
                          <td className="py-3 px-4">{location.name}</td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                            {location.description || 'No description'}
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                location.is_active
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}
                            >
                              {location.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditLocation(location)}
                                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteLocation(location.id)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Location Form */}
            {showLocationForm && (
              <div
                className={`rounded-xl shadow-lg p-6 ${
                  isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
                }`}
              >
                <h3 className="text-lg font-semibold mb-4">
                  {editingLocation ? 'Edit Location' : 'Add New Location'}
                </h3>

                <form onSubmit={handleLocationSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name *</label>
                    <input
                      type="text"
                      value={newLocation.name}
                      onChange={e => setNewLocation({ ...newLocation, name: e.target.value })}
                      required
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-800'
                      }`}
                      placeholder="Enter location name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <textarea
                      value={newLocation.description}
                      onChange={e =>
                        setNewLocation({ ...newLocation, description: e.target.value })
                      }
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-800'
                      }`}
                      placeholder="Enter location description"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={newLocation.is_active}
                      onChange={e =>
                        setNewLocation({ ...newLocation, is_active: e.target.checked })
                      }
                      className="mr-2"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium">
                      Active
                    </label>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                    >
                      {editingLocation ? 'Update' : 'Create'} Location
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowLocationForm(false);
                        setEditingLocation(null);
                        setNewLocation({ name: '', description: '', is_active: true });
                      }}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Settings;
